import { EYES, REFRACTION_OUTCOMES, REFRACTION_TESTERS, VISUAL_ACUITY_NOTATIONS, } from './enums.js';
import { InvalidVisualAcuity, toLogMar } from './visual-acuity.js';
/** The top-level names a refraction request may carry. Anything else is refused by name. */
export const REFRACTION_FIELDS = [
    'branchId',
    'performedAt',
    'tester',
    'outcome',
    'referredTo',
    'referredOn',
    'nearAdd',
    'workingDistanceCm',
    'dominantEye',
    'pdBinocularMm',
    'od',
    'os',
];
/** The names one eye of a request may carry. */
export const REFRACTION_EYE_FIELDS = [
    'objective',
    'subjective',
    'oldGlasses',
    'unaided',
    'aided',
    'pinhole',
    'pdMonocularMm',
];
/** The names the tester block may carry. */
export const REFRACTION_TESTER_FIELDS = ['kind', 'staffId', 'name', 'registration'];
const text = (value) => typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
export const isRefractionTester = (value) => REFRACTION_TESTERS.includes(value);
export const isRefractionOutcome = (value) => REFRACTION_OUTCOMES.includes(value);
export const isVisualAcuityNotation = (value) => VISUAL_ACUITY_NOTATIONS.includes(value);
export const isEyeName = (value) => EYES.includes(value);
const refuse = (code, message) => ({ ok: false, code, message });
/**
 * `REF-002`: "a required, typed field, not free text", with different capture per value.
 *
 * | kind | captured |
 * |---|---|
 * | `in_house_doctor` | the staff member, and a registration number |
 * | `in_house_refractionist` | the staff member (their name is the staff record's) |
 * | `visiting_optometrist` | name and registration. Not staff |
 * | `external_prescriber` | prescriber name and registration where legible. Both optional |
 * | `customer_supplied_unknown` | nothing. The kind is the flag |
 *
 * Keeps only what the kind captures. A name sent with `customer_supplied_unknown` is dropped from
 * the result, because that value means no identifiable prescriber and a name beside it would make
 * the flag lie.
 */
export function resolveTester(input) {
    const kind = input?.kind;
    if (!isRefractionTester(kind)) {
        return refuse('REF_TESTER_INVALID', `Say who did the test. One of: ${REFRACTION_TESTERS.join(', ')}.`);
    }
    const staffId = text(input?.staffId);
    const name = text(input?.name);
    const registration = text(input?.registration);
    switch (kind) {
        case 'in_house_doctor':
            if (staffId === null) {
                return refuse('REF_TESTER_STAFF_REQUIRED', 'Say which member of staff did the test.');
            }
            if (registration === null) {
                return refuse('REF_TESTER_REGISTRATION_REQUIRED', 'A doctor or optometrist needs a registration number.');
            }
            return { ok: true, tester: { kind, staffId, name: null, registration } };
        case 'in_house_refractionist':
            if (staffId === null) {
                return refuse('REF_TESTER_STAFF_REQUIRED', 'Say which member of staff did the test.');
            }
            return { ok: true, tester: { kind, staffId, name: null, registration: null } };
        case 'visiting_optometrist':
            if (name === null) {
                return refuse('REF_TESTER_NAME_REQUIRED', 'A visiting optometrist needs a name.');
            }
            if (registration === null) {
                return refuse('REF_TESTER_REGISTRATION_REQUIRED', 'A visiting optometrist needs a registration number.');
            }
            return { ok: true, tester: { kind, staffId: null, name, registration } };
        case 'external_prescriber':
            return { ok: true, tester: { kind, staffId: null, name, registration } };
        case 'customer_supplied_unknown':
            return { ok: true, tester: { kind, staffId: null, name: null, registration: null } };
    }
}
/** A real calendar day written `yyyy-mm-dd`. */
export function isCalendarDay(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const d = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}
/**
 * `REF-006`: a referral shows "that a referral was made, to whom, and on what date". Nothing more.
 *
 * `referred_out` needs both; any other outcome carries neither, because a destination beside "no
 * change" is a record that contradicts itself. The database holds the first half as a check
 * (`refraction_referral_names_destination`); this is what turns its violation into a sentence.
 */
export function referralProblem(outcome, referredTo, referredOn) {
    const to = text(referredTo);
    const hasOn = referredOn !== undefined && referredOn !== null && referredOn !== '';
    if (outcome === 'referred_out') {
        if (to === null) {
            return {
                code: 'REF_REFERRAL_DESTINATION_REQUIRED',
                message: 'A referral names who the customer was sent to.',
            };
        }
        if (!isCalendarDay(referredOn)) {
            return {
                code: 'REF_REFERRAL_DATE_REQUIRED',
                message: 'A referral has the day it was made, as yyyy-mm-dd.',
            };
        }
        return null;
    }
    if (to !== null || hasOn) {
        return {
            code: 'REF_REFERRAL_WITHOUT_OUTCOME',
            message: 'A destination and date belong only to a record whose outcome is referred out.',
        };
    }
    return null;
}
/**
 * `REF-003`: every acuity carries the notation it was written in, "which is the point: a bare 6/9
 * means nothing without it". Parsed through `toLogMar`, so a value that cannot be read (a typo, or
 * 6/9 written as 6-9) is refused here and not stored, where `aidedIsWorseThanUnaided` would later
 * throw on it.
 */
export function visualAcuityProblem(value, notation) {
    if (typeof value !== 'string' || value.trim() === '') {
        return { code: 'REF_VA_INVALID', message: 'A visual acuity needs a value such as 6/9.' };
    }
    if (!isVisualAcuityNotation(notation)) {
        return {
            code: 'REF_VA_NOTATION_REQUIRED',
            message: `Say how ${value.trim()} is written. One of: ${VISUAL_ACUITY_NOTATIONS.join(', ')}.`,
        };
    }
    try {
        toLogMar({ value: value.trim(), notation });
    }
    catch (error) {
        if (error instanceof InvalidVisualAcuity) {
            return {
                code: 'REF_VA_INVALID',
                message: `${value.trim()} is not a readable ${notation} acuity.`,
            };
        }
        throw error;
    }
    return null;
}
//# sourceMappingURL=refraction-rules.js.map